import { motion } from 'framer-motion';
import { FileText, CheckCircle2, RefreshCw, Eye, ArrowRight } from 'lucide-react';

const HeroAnimation = () => {
  return (
    <div className="relative w-full px-6" data-testid="hero-animation">
      {/* Glow backdrop */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-orange-500/10 blur-3xl scale-125 -z-10" />

      {/* Browser frame */}
      <motion.div
        className="relative bg-slate-900 rounded-2xl shadow-2xl border border-slate-800 overflow-hidden"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-2.5 bg-slate-800/60 border-b border-slate-700/50">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
          </div>
          <div className="flex-1 flex justify-center">
            <div className="px-3 py-0.5 bg-slate-700/40 rounded text-[10px] text-slate-500 font-mono">
              app.plouton.co/workflows
            </div>
          </div>
        </div>

        {/* Dashboard content */}
        <div className="p-4 sm:p-5 space-y-4">
          {/* Top bar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-md bg-blue-600 flex items-center justify-center">
                <span className="text-[8px] font-bold text-white">P</span>
              </div>
              <span className="text-xs font-semibold text-white">AP Workflow</span>
            </div>
            <motion.div
              className="flex items-center gap-1.5 px-2 py-1 bg-green-500/15 rounded-full"
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
              <span className="text-[10px] text-green-400 font-medium">Running</span>
            </motion.div>
          </div>

          {/* Workflow pipeline */}
          <div className="space-y-2.5">
            {/* Step 1 - Invoice Ingestion - Complete */}
            <WorkflowStep
              icon={FileText}
              label="Invoice Ingestion"
              sublabel="INV-2847 from Ariba"
              status="complete"
              delay={0.8}
            />

            {/* Animated connector */}
            <ConnectorLine delay={1.4} />

            {/* Step 2 - Three-Way Matching - Active */}
            <WorkflowStep
              icon={RefreshCw}
              label="Three-Way Matching"
              sublabel="PO #4821 ↔ GRN ↔ Invoice"
              status="active"
              delay={1.6}
            />

            {/* Animated connector */}
            <ConnectorLine delay={2.4} />

            {/* Step 3 - Audit & Approve - Pending */}
            <WorkflowStep
              icon={Eye}
              label="Audit & Approve"
              sublabel="Human checkpoint"
              status="pending"
              delay={2.8}
            />
          </div>

          {/* Bottom stats bar */}
          <motion.div
            className="flex items-center justify-between pt-3 border-t border-slate-800"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 3.2 }}
          >
            <div className="flex items-center gap-3">
              <StatPill label="Processed" value="142" />
              <StatPill label="Accuracy" value="99.2%" />
            </div>
            <div className="text-[10px] text-slate-600 font-mono">
              12 min ago
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Floating cards */}
      <FloatingCard
        className="-left-5 top-[8%]"
        delay={1.2}
        icon={FileText}
        text="Invoice extracted"
        subtext="$12,450.00"
        color="blue"
      />

      <FloatingCard
        className="-right-4 bottom-[12%]"
        delay={2.2}
        icon={CheckCircle2}
        text="Match confirmed"
        subtext="PO #4821"
        color="green"
      />
    </div>
  );
};

const WorkflowStep = ({ icon: Icon, label, sublabel, status, delay }) => {
  const statusStyles = {
    complete: {
      bg: 'bg-slate-800/50 border-green-500/30',
      iconBg: 'bg-green-500/20',
      iconColor: 'text-green-400',
      indicator: <CheckCircle2 className="w-3.5 h-3.5 text-green-400" />,
    },
    active: {
      bg: 'bg-blue-500/10 border-blue-500/40',
      iconBg: 'bg-blue-500/20',
      iconColor: 'text-blue-400',
      indicator: (
        <motion.div
          className="w-3.5 h-3.5 rounded-full border-2 border-blue-400 border-t-transparent"
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
        />
      ),
    },
    pending: {
      bg: 'bg-slate-800/30 border-slate-700/40',
      iconBg: 'bg-slate-700/30',
      iconColor: 'text-slate-500',
      indicator: <div className="w-3.5 h-3.5 rounded-full border border-slate-600" />,
    },
  };

  const s = statusStyles[status];

  return (
    <motion.div
      className={`flex items-center gap-3 p-3 rounded-xl border ${s.bg} transition-all`}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <div className={`w-8 h-8 rounded-lg ${s.iconBg} flex items-center justify-center flex-shrink-0`}>
        <Icon className={`w-4 h-4 ${s.iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-white">{label}</div>
        <div className="text-[10px] text-slate-500 truncate">{sublabel}</div>
      </div>
      {s.indicator}

      {/* Progress bar for active */}
      {status === 'active' && (
        <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-slate-800 rounded-b-xl overflow-hidden">
          <motion.div
            className="h-full bg-blue-500"
            initial={{ width: '0%' }}
            animate={{ width: '75%' }}
            transition={{ duration: 3, delay: delay + 0.3, ease: 'easeOut' }}
          />
        </div>
      )}
    </motion.div>
  );
};

const ConnectorLine = ({ delay }) => (
  <div className="flex justify-center">
    <motion.div
      className="w-px h-4 bg-slate-700"
      initial={{ scaleY: 0 }}
      animate={{ scaleY: 1 }}
      transition={{ duration: 0.3, delay }}
      style={{ originY: 0 }}
    />
  </div>
);

const StatPill = ({ label, value }) => (
  <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-800/60 rounded">
    <span className="text-[10px] text-slate-500">{label}</span>
    <span className="text-[10px] font-semibold text-white">{value}</span>
  </div>
);

const FloatingCard = ({ className, delay, icon: Icon, text, subtext, color }) => {
  const colors = {
    blue: { bg: 'bg-blue-500/10 border-blue-500/20', icon: 'text-blue-400' },
    green: { bg: 'bg-green-500/10 border-green-500/20', icon: 'text-green-400' },
  };
  const c = colors[color];

  return (
    <motion.div
      className={`absolute ${className} z-10`}
      initial={{ opacity: 0, scale: 0.8, y: 10 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
    >
      <motion.div
        className={`flex items-center gap-2 px-3 py-2 rounded-lg border backdrop-blur-xl shadow-xl ${c.bg}`}
        animate={{ y: [0, -4, 0] }}
        transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <Icon className={`w-3.5 h-3.5 ${c.icon} flex-shrink-0`} />
        <div>
          <div className="text-[10px] font-medium text-white whitespace-nowrap">{text}</div>
          <div className="text-[9px] text-slate-400">{subtext}</div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default HeroAnimation;
